import { useEffect } from 'react';
import { Form, Input, Select, InputNumber, Switch, Button } from '@/shared/ui/primitives';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { cityApi } from '../api/city.api';
import { cityKeys } from '../api/city.keys';
import type { City, CityFormData } from '@/shared/types';

interface CityFormProps {
  initialData?: City;
  onSubmit: (data: CityFormData) => void;
  isLoading: boolean;
}

export default function CityForm({ initialData, onSubmit, isLoading }: CityFormProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const countryId = Form.useWatch('countryId', form);

  const { data: countries = [], isLoading: loadingCountries } = useQuery({
    queryKey: cityKeys.countries,
    queryFn: () => cityApi.listCountries(),
  });

  const { data: regions = [], isLoading: loadingRegions } = useQuery({
    queryKey: cityKeys.regionsByCountry(countryId),
    queryFn: () => cityApi.listRegionsByCountry(countryId),
    enabled: !!countryId,
  });

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        nameEn: initialData.nameEn || '',
        nameAr: initialData.nameAr || '',
        countryId: initialData.countryId || undefined,
        regionId: initialData.regionId || undefined,
        sortIndex: initialData.sortIndex ?? 0,
        isActive: initialData.isActive ?? true,
      });
    }
  }, [initialData, form]);

  const handleCountryChange = () => {
    form.setFieldValue('regionId', undefined);
  };

  const handleFinish = (values: any) => {
    const { countryId: _c, ...data } = values;
    onSubmit(data);
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish} initialValues={{ isActive: true, sortIndex: 0 }} style={{ maxWidth: 480 }}>
      <Form.Item name="nameEn" label={t('cities.nameEn')} rules={[{ required: true }]}>
        <Input placeholder={t('cities.nameEnPlaceholder')} />
      </Form.Item>
      <Form.Item name="nameAr" label={t('cities.nameAr')} rules={[{ required: true }]}>
        <Input dir="rtl" placeholder={t('cities.nameArPlaceholder')} />
      </Form.Item>
      <Form.Item name="countryId" label={t('cities.country')} rules={[{ required: true }]}>
        <Select placeholder={t('cities.selectCountry')} loading={loadingCountries} onChange={handleCountryChange} options={countries.map((c) => ({ value: c.id, label: c.nameEn }))} />
      </Form.Item>
      <Form.Item name="regionId" label={t('cities.region')} rules={[{ required: true }]}>
        <Select placeholder={!countryId ? t('cities.selectCountryFirst') : t('cities.selectRegion')} loading={loadingRegions} disabled={!countryId} options={regions.map((r) => ({ value: r.id, label: r.nameEn }))} />
      </Form.Item>
      <Form.Item name="sortIndex" label={t('cities.sortIndex')} rules={[{ required: true }]}>
        <InputNumber style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="isActive" label={t('common.active')} valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading}>{t('common.save')}</Button>
      </Form.Item>
    </Form>
  );
}
